import React, { useEffect, useState } from 'react';
import { Builder } from '@builder.io/react';
import { useSelector, useDispatch } from 'react-redux';
import { useKeenSlider } from 'keen-slider/react';
import { selectSelectedCurrency } from '../../../../../../store/user';
import ProductBox from '../../../../../general/productBox';
import AppealFundraiserProductBoxV2 from './appealFundraiserProductBoxV2';
import { LiaAngleLeftSolid, LiaAngleRightSolid } from 'react-icons/lia';
import { selectProducts, setLastOrdersV2 } from '../../../../../../store/products';
import services from '../../../../../../services';

const AppealWidgetCarouselV2 = props => {
  /* ----------------------------- Start variables ---------------------------- */
  const {
    items = [
      {
        isFundraiser: true,
        defaultProduct: { 
          img: '', 
          creator: '', 
          label: '', 
          description: '',
          isDynamicProduct: false,
          staticLinkText: 'Donate Now',
        },
        link: '',
        goal: '',
        startDate: '',
        endDate: '',
        targetDate: '',
        appealsProducts: [],
      },
    ],
  } = props;
  const selectedCurrencyStore = useSelector(selectSelectedCurrency);
  const _allProducts = useSelector(state => selectProducts(state));
  const dispatch = useDispatch();
  const [loaded, setLoaded] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [allAppeals, setAllAppeals] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [pause, setPause] = useState(false);
  const MutationPlugin = slider => {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        slider.update();
      });
    });
    const config = { childList: true };

    slider.on('created', () => {
      observer.observe(slider.container, config);
    });
    slider.on('destroyed', () => {
      observer.disconnect();
    });
  };

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      mode: 'snap',
      loop: true,
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      created() {
        setLoaded(true);
      },
      breakpoints: {
        '(min-width: 320px)': {
          slides: { perView: 1, spacing: 10 },
        },
        '(min-width: 768px)': {
          slides: { perView: 2, spacing: 10 },
        },
        '(min-width: 992px)': {
          slides: { perView: 4, spacing: 16 },
        },
      },
      slides: {
        origin: 'auto',
        // perView: 1,
        // spacing: 8,
      },
    },
    [MutationPlugin],
  );
  const showSliderDesktop = items.length > 3;
  const PRODUCTS_KEY_PREFIX = 'latest_orders_specific_';
  const PRODUCTS_TIMESTAMP_PREFIX = 'latest_orders_timestamp_specific_';
  const ONE_DAY = 1000 * 60 * 60 * 24; // 24h
  /* -------------------------------------------------------------------------- */

  /* ------------------------------- Start Methods ---------------------------- */
  const getLatestOrdersVersion = async (currency, allAppeals) => {
    // console.log('in methode=', currency, allAppeals);
    const cacheKey = PRODUCTS_KEY_PREFIX + currency;
    const timeKey = PRODUCTS_TIMESTAMP_PREFIX + currency;

    const cachedRaw = localStorage.getItem(cacheKey);
    const timestamp = localStorage.getItem(timeKey);

    let cached;
    try {
      cached = cachedRaw ? JSON.parse(cachedRaw) : null;
    } catch {
      cached = null;
    }
    // Check if cached creators cover all current appeals
    const cachedCreators = new Set(cached?.data?.map(p => p.creator) || []);
    const allAppealsCreators = new Set(allAppeals.flatMap(item => item.appealsProducts.map(ap => ap.creator)));
    const hasNewCreators = [...allAppealsCreators].some(c => !cachedCreators.has(c));
    // If cache exists, timestamp is valid, and no new creators, use cache
    if (cached && timestamp && Date.now() - Number(timestamp) < ONE_DAY && !hasNewCreators) {
      // console.log('using cached data',cached);
      dispatch(setLastOrdersV2(cached.data));
      return;
    }
    const formatDate = date => new Date(date).toISOString().split('T')[0];
    // Otherwise fetch from API
    try {
      const req = {
        currency: currency,
        products: allAppeals.flatMap(item =>
          item.appealsProducts.map(ap => ({
            id: ap.creator,
            start_date: item.startDate ? formatDate(item.startDate) : new Date(Date.now() - 360 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Default to 360 days ago from now
            end_date: item.endDate ? formatDate(item.endDate) : new Date().toISOString().split('T')[0], // Default to Today in YYYY-MM-DD
          })),
        ),
      };

      const response = await services.getLatestOrdersV4(req);
      let _first = response.data;

      _first[0] = {
        ..._first[0],
        currency,
      };
      console.log('in new widgets v2====', response, _first);

      // Save to Redux
      dispatch(setLastOrdersV2(_first));

      // Save to localStorage
      localStorage.setItem(cacheKey, JSON.stringify(_first));
      localStorage.setItem(timeKey, Date.now());
    } catch (error) {
      console.error('error========', error);
    }
  };
  /* -------------------------------------------------------------------------- */

  /* ------------------------------- Start hooks ------------------------------ */

  useEffect(() => {
    if (_allProducts && Object.keys(_allProducts).length) {
      const processedItems = [];
      const appealsItems = [];
      items.forEach(item => {
        // console.log('all items==', item);
        if (item.isFundraiser) {
          const tempItem = {
            defaultProduct: item.defaultProduct,
            link: item.link,
            goal: item.goal || 1000000,
            appealsProducts: item.appealsProducts || [],
            startDate: item.startDate || new Date().toISOString() - 360 * 24 * 60 * 60 * 1000, // Default to 360 days ago from now
            targetDate: item.targetDate || new Date().toISOString() + 30 * 24 * 60 * 60 * 1000, // Default to 30 days from now
            isFundraiser: true,
          };
          processedItems.push(tempItem);
          appealsItems.push({
            startDate: tempItem.startDate,
            targetDate: tempItem.targetDate,
            appealsProducts: [...tempItem.appealsProducts, { creator: item.defaultProduct?.creator }],
          });
        } else {
          let product = '';
          _allProducts.forEach(_item => {
            _item.sub_categories.forEach(innerItem => {
              innerItem.products.forEach(innerProduct => {
                if (innerProduct.creator === item.defaultProduct.creator) {
                  product = innerProduct;
                }
              });
            });
          });
          if (product) {
            const newProduct = {
              ...product,
              image_link: item.defaultProduct.img?.src || product.image_link || product.image,
              name: item.defaultProduct.label || product.name,
              description: item.defaultProduct.description || product.description,
              short_description: item.defaultProduct.description || product.short_description,
              isStaticProduct:!item.defaultProduct.isDynamicProduct,
              staticLinkText:item.defaultProduct.staticLinkText
            };
            const newItem = {
              defaultProduct: newProduct,
              link: item.link || `/appeals/${product.name}`,
              isFundraiser: false,
            };
            // setAllItems(prevItems => [...prevItems, newItem]);
            processedItems.push(newItem);
          } else {
            console.error(`Product with creator ID ${item.defaultProduct.creator} not found in allProducts.`);
          }
        }
        return null; // Return null to avoid React warning about missing return in map
      });
      setAllAppeals(appealsItems);
      console.log('processedItems=============================', processedItems);
      setAllItems(processedItems);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, _allProducts]); // Dependencies remain the same

  // Autoplay effect
  useEffect(() => {
    // if (!instanceRef.current) return;
    if (!loaded) return;

    const autoplay = setInterval(() => {
      if (!pause && instanceRef.current) {
        instanceRef?.current?.next();
      }
    }, 8000); // Change slide every 8 seconds

    return () => clearInterval(autoplay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pause, loaded, currentSlide]); // Added currentSlide to reset timer after swipe
  useEffect(() => {
    allAppeals.length && getLatestOrdersVersion(selectedCurrencyStore, allAppeals);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAppeals, selectedCurrencyStore]); // Added currentSlide to reset timer after swipe
  /* -------------------------------------------------------------------------- */
  return (
    <section className={`w-full py-2 md:py-4`}>
      <div className="md:container w-full mx-auto">
        <div className={`w-full flex-wrap justify-center items-center relative ${showSliderDesktop ? 'true-sliderDesktop' : 'false-sliderDesktop'}`}>
          {/* Show slider for both desktop & mobile if more than 3 */}
          {/* {console.log('length of items==', allItems)} */}
          {showSliderDesktop ? (
            <>
              {/* Navigation arrows */}
              <div className="flex">
                {instanceRef.current && (
                  <div
                    className="absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[47%] -left-[0px] md:-left-[16px] xl:-left-[32px] z-10 cursor-pointer text-stone-600 bg-white bg-opacity-70 rounded-full"
                    onClick={e => e.stopPropagation() || instanceRef.current?.prev()}
                  >
                    <LiaAngleLeftSolid size={25} />
                  </div>
                )}
                {instanceRef.current && (
                  <div
                    className="absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[47%] -right-[0px] md:-right-[16px] xl:-right-[32px] z-10 cursor-pointer text-stone-600 bg-white bg-opacity-70 rounded-full"
                    onClick={e => e.stopPropagation() || instanceRef.current?.next()}
                  >
                    <LiaAngleRightSolid size={25} />
                  </div>
                )}
              </div>
              {/* Slider for both mobile and desktop */}
              <div ref={sliderRef} className="keen-slider" onMouseEnter={() => setPause(true)} onMouseLeave={() => setPause(false)}>
                {allItems.map((item, index) => (
                  <div
                    key={`active_carousel_${index}`}
                    className="keen-slider__slide basis-full flex justify-center w-full sm:max-w-[300px] sm:basis-[calc(50%-8px)]"
                  >
                    {/* Product content here */}
                    {item.isFundraiser ? (
                      <AppealFundraiserProductBoxV2
                        defaultProduct={item.defaultProduct}
                        link={item.link}
                        goal={item.goal}
                        appealsProducts={item.appealsProducts}
                        targetDate={item.targetDate}
                      />
                    ) : (
                      <ProductBox
                        product={item.defaultProduct}
                        currency={selectedCurrencyStore}
                        btnContainerClass="h-[106px]"
                        titleClasses="uppercase px-1 !text-lg !h-auto !mt-3 min-h-[56px]"
                        childClasses="!rounded-none !p-0 !pb-3 md:!pb-5 md:!p-0"
                        imageContainerClass="!rounded-none"
                        imageClass="!rounded-none"
                        descriptionStyle="px-2 md:px-3"
                        skeletonStyle="px-2"
                        squareButton
                        isStaticProduct={item.defaultProduct.isStaticProduct}
                        staticLink={item.link}
                        staticLinkText={item.defaultProduct.staticLinkText}
                        addToCart
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              {/* Grid on desktop */}
              <div className="hidden md:flex flex-wrap gap-3 sm:gap-4 justify-center items-start w-full">
                {allItems.map((item, index) => (
                  <div key={`grid_item_${index}`} className="w-full sm:max-w-[300px] sm:basis-[calc(50%-8px)]">
                    {/* Product content (same as above) */}
                    {item.isFundraiser ? (
                      <AppealFundraiserProductBoxV2
                        defaultProduct={item.defaultProduct}
                        link={item.link}
                        goal={item.goal}
                        appealsProducts={item.appealsProducts}
                        targetDate={item.targetDate}
                      />
                    ) : (
                      <ProductBox
                        product={item.defaultProduct}
                        currency={selectedCurrencyStore}
                        btnContainerClass="h-[106px]"
                        titleClasses="uppercase px-1 !text-lg !h-auto !mt-3 min-h-[56px]"
                        childClasses="!rounded-none !p-0 !pb-3 md:!pb-5 md:!p-0"
                        imageContainerClass="!rounded-none"
                        imageClass="!rounded-none"
                        descriptionStyle="px-2 md:px-3"
                        skeletonStyle="px-2"
                        squareButton
                        isStaticProduct={item.defaultProduct.isStaticProduct}
                        staticLink={item.link}
                        staticLinkText={item.defaultProduct.staticLinkText}
                        addToCart
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex md:hidden">
                {instanceRef.current && (
                  <div
                    className="absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[47%] -left-[0px] md:-left-[16px] xl:-left-[32px] z-10 cursor-pointer text-stone-600 bg-white bg-opacity-70 rounded-full"
                    onClick={e => e.stopPropagation() || instanceRef.current?.prev()}
                  >
                    <LiaAngleLeftSolid size={25} />
                  </div>
                )}
                {instanceRef.current && (
                  <div
                    className="absolute flex justify-center items-center translate-y-1/2 w-8 h-8 top-[47%] -right-[0px] md:-right-[16px] xl:-right-[32px] z-10 cursor-pointer text-stone-600 bg-white bg-opacity-70 rounded-full"
                    onClick={e => e.stopPropagation() || instanceRef.current?.next()}
                  >
                    <LiaAngleRightSolid size={25} />
                  </div>
                )}
              </div>
              {/* Slider only on mobile (md:!hidden) */}
              <div ref={sliderRef} className="keen-slider md:!hidden" onMouseEnter={() => setPause(true)} onMouseLeave={() => setPause(false)}>
                {allItems.map((item, index) => (
                  <div
                    key={`mobile_slider_${index}`}
                    className="keen-slider__slide basis-full flex justify-center w-full sm:max-w-[300px] sm:basis-[calc(50%-8px)]"
                  >
                    {/* Same content as above */}
                    {item.isFundraiser ? (
                      <AppealFundraiserProductBoxV2
                        defaultProduct={item.defaultProduct}
                        link={item.link}
                        goal={item.goal}
                        appealsProducts={item.appealsProducts}
                        targetDate={item.targetDate}
                      />
                    ) : (
                      <ProductBox
                        product={item.defaultProduct}
                        currency={selectedCurrencyStore}
                        btnContainerClass="h-[106px]"
                        titleClasses="uppercase px-1 !text-lg !h-auto !mt-3 min-h-[56px]"
                        childClasses="!rounded-none !p-0 !pb-3 md:!pb-5 md:!p-0"
                        imageContainerClass="!rounded-none"
                        imageClass="!rounded-none"
                        descriptionStyle="px-2 md:px-3"
                        skeletonStyle="px-2"
                        squareButton
                        isStaticProduct={item.defaultProduct.isStaticProduct}
                        staticLink={item.link}
                        staticLinkText={item.defaultProduct.staticLinkText}
                        addToCart
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

Builder.registerComponent(AppealWidgetCarouselV2, {
  name: 'AppealWidgetCarouselV2',
  inputs: [
    {
      name: 'items',
      type: 'list',
      defaultValue: [],
      minRows: 0,
      maxRows: 6,
      defaultItem: {
        isFundraiser: true,
        defaultProduct: {
          img: '',
          creator: '',
          label: '',
          description: '',
          isDynamicProduct: false,
          staticLinkText: 'Donate Now',
        },
        link: '',
        goal: '',
        targetDate: '',
        appealsProducts: [],
      },
      subFields: [
        {
          name: 'isFundraiser',
          type: 'boolean',
          friendlyName: 'Is Fundraiser Box?',
          defaultValue: true,
          helpText: 'Toggle to switch between Fundraiser and simple Product Box',
        },
        {
          name: 'defaultProduct',
          type: 'object',
          friendlyName: 'Main Product Info',
          subFields: [
            {
              name: 'img',
              type: 'object',
              description: 'If provided, this image will display instead of the product image.',
              subFields: [
                { name: 'src', type: 'file', allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg'] },
                { name: 'alt', type: 'string' },
              ],
              required: false,
            },
            {
              name: 'creator',
              type: 'string',
              helperText: 'Enter product creator ID',
              required: true,
              hideFromUI: false,
            },
            {
              name: 'label',
              type: 'string',
              helperText: 'Display name for the product',
              required: false,
              hideFromUI: false,
              friendlyName: 'Product Label',
            },
            {
              name: 'description',
              type: 'string',
              required: false,
              hideFromUI: false,
              friendlyName: 'Product Description',
            },
            {
              name: 'isDynamicProduct',
              type: 'boolean',
              defaultValue: false,
              helperText: 'Toggle to switch between Static and Dynamic Product Box',
              friendlyName: 'Dynamic Product Box',
            },
            {
              name: 'staticLinkText',
              type: 'string',
              helperText: 'Static Link Text',
              required: false,
              hideFromUI: false,
              friendlyName: 'Give Text to button',
              defaultValue: 'Donate Now',
            },
          ],
        },
        {
          name: 'link',
          label: 'Link',
          type: 'string',
          helperText: 'Details page Link (e.g. /appeals/palestine-emergency)',
          required: false,
        },
        {
          name: 'goal',
          label: 'Goal',
          type: 'number',
          helperText: 'Goal amount for the fundraiser (e.g. 1000000)',
          defaultValue: 1000000,
          required: false,
          showIf: options => options.get('isFundraiser') === true,
        },
        {
          name: 'startDate',
          type: 'date',
          defaultValue: '',
          helperText: 'Start Date for the fundraiser (e.g. 2025-01-01T00:00)',
          friendlyName: 'Start Date',
          required: false,
          showIf: options => options.get('isFundraiser') === true,
        },
        {
          name: 'endDate',
          type: 'date',
          defaultValue: '',
          helperText: 'End Date for the fundraiser (e.g. 2025-01-01T00:00)',
          friendlyName: 'End Date',
          required: false,
          showIf: options => options.get('isFundraiser') === true,
        },
        {
          name: 'targetDate',
          type: 'date',
          defaultValue: '',
          helperText: 'Target Date for the fundraiser (e.g. 2025-01-01T00:00)',
          friendlyName: 'Target Date',
          required: false,
          showIf: options => options.get('isFundraiser') === true,
        },
        {
          name: 'appealsProducts',
          type: 'array',
          helperText: 'Products to include in the Fundraiser calculation',
          required: false,
          showIf: options => options.get('isFundraiser') === true,
          subFields: [
            {
              name: 'creator',
              type: 'string',
            },
          ],
        },
      ],
    },
  ],
});

export default AppealWidgetCarouselV2;
